import React, { useMemo } from 'react';
import clsx from 'clsx';
import { AlertCircle, Pencil, X } from 'lucide-react';
import { WModal } from 'shared_components/components/WModal/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { countryCodes } from 'shared_components/utils/Countries';
import type { Row } from '../MasterDiscographyTypes';
import {
  seedArtistsFromRow,
  type MassEdits,
  type MassRowEdits,
} from './MassEditFieldModals';
import { liveToString } from '../../../utils/recordingValidators';

type MassFieldKey = keyof MassRowEdits;

/**
 * Mass-edit summary / confirmation modal.
 *
 * Before anything is applied, the user reviews every pending change grouped by
 * recording (field: before → after). "Confirm" hands off to the batch apply
 * (see massEditBatch.ts), which sends one request per endpoint — correct
 * recording data, change associated artists, manage contributions — each
 * carrying a list of per-recording envelopes (contract §3.1).
 *
 * Because the batch endpoints report success per recording, a partial failure
 * leaves this modal open: the recordings that saved disappear from the list and
 * the ones that failed stay, annotated with the reason the backend gave.
 */

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const DASH = '—';

/** Country of Recording is stored as an alpha-3 code; show the name the user
 *  picked, falling back to the raw code for anything not in the list. */
const countryName = (code: string | null | undefined): string => {
  if (!code) return DASH;
  return countryCodes.find((c) => c.code === code)?.name ?? code;
};

interface FieldChange {
  field: MassFieldKey;
  label: string;
  before: string;
  after: string;
}

interface RecordingChange {
  id: string;
  title: string;
  changes: FieldChange[];
}

/** Build the human-readable before/after list for one recording. */
function describeRow(row: Row, edits: MassEdits[string]): FieldChange[] {
  const changes: FieldChange[] = [];

  if (edits.title !== undefined) {
    changes.push({
      field: 'title',
      label: 'Recording Title',
      before: row.title || DASH,
      after: edits.title || DASH,
    });
  }
  if (edits.duration !== undefined) {
    changes.push({
      field: 'duration',
      label: 'Duration',
      before: row.duration || DASH,
      after: edits.duration || DASH,
    });
  }
  if (edits.live !== undefined) {
    const beforeDescriptor = liveToString(row.live);
    changes.push({
      field: 'live',
      label: 'Live',
      before: row.isLive
        ? beforeDescriptor
          ? `Live · ${beforeDescriptor}`
          : 'Live'
        : 'Not live',
      after: edits.live.trim() ? `Live · ${edits.live.trim()}` : 'Not live',
    });
  }
  if (edits.version !== undefined) {
    changes.push({
      field: 'version',
      label: 'Version',
      before: (row.versionsArray ?? []).join(', ') || DASH,
      after: edits.version.join(', ') || DASH,
    });
  }
  if (edits.isrcs !== undefined) {
    changes.push({
      field: 'isrcs',
      label: 'ISRCs',
      before: (row.isrcsArray ?? []).join(', ') || DASH,
      after: edits.isrcs.join(', ') || DASH,
    });
  }
  if (edits.contributions !== undefined) {
    changes.push({
      field: 'contributions',
      label: 'Contributions',
      before:
        (row.contributions ?? [])
          .map((c) => capitalize(c.contribution))
          .join(', ') || DASH,
      after:
        edits.contributions.map((c) => capitalize(c.contribution)).join(', ') ||
        DASH,
    });
  }
  if (edits.yearRecorded !== undefined) {
    changes.push({
      field: 'yearRecorded',
      label: 'Year of Recording',
      before: row.yearRecorded || DASH,
      after: edits.yearRecorded.trim() || DASH,
    });
  }
  if (edits.location !== undefined) {
    changes.push({
      field: 'location',
      label: 'Country of Recording',
      before: countryName(row.location),
      after: countryName(edits.location),
    });
  }
  if (edits.pNames !== undefined) {
    changes.push({
      field: 'pNames',
      label: '(P) Name',
      before: (row.pNames ?? []).join(', ') || DASH,
      after: edits.pNames.join(', ') || DASH,
    });
  }
  if (edits.artist !== undefined) {
    changes.push({
      field: 'artist',
      label: 'Artists',
      before:
        seedArtistsFromRow(row)
          .map((a) => a.name)
          .join(', ') ||
        (row.artist ?? DASH),
      // Demoted artists are tombstones in the pending value — they still get a
      // delete+nfa edit, but the list only ever shows featured / other-featured
      // artists, so they drop out of the "after" just as they do in the editor.
      after:
        edits.artist
          .filter((a) => !a.demoted)
          .map((a) => a.name)
          .join(', ') || DASH,
    });
  }

  return changes;
}

interface MassEditSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  edits: MassEdits;
  rows: Row[];
  /** Open the field editor for a pending change (a last-minute adjustment). */
  onEditChange: (rowId: string, field: MassFieldKey) => void;
  /** Discard a single pending change. */
  onRemoveChange: (rowId: string, field: MassFieldKey) => void;
  /** True while the batch apply is in flight — locks the modal's actions. */
  isSaving?: boolean;
  /** Per-recording failures from the last apply (recording id → reasons). */
  errorsByRecording?: Record<string, string[]>;
  /** A transport/validation failure that took out a whole batch. */
  fatalError?: string;
}

export const MassEditSummaryModal: React.FC<MassEditSummaryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  edits,
  rows,
  onEditChange,
  onRemoveChange,
  isSaving = false,
  errorsByRecording,
  fatalError,
}) => {
  const { recordings, fieldChangeCount } = useMemo(() => {
    const rowById = new Map(rows.map((r) => [r.id, r]));
    const result: RecordingChange[] = [];
    let count = 0;
    Object.entries(edits).forEach(([id, rowEdits]) => {
      const row = rowById.get(id);
      if (!row) return;
      const changes = describeRow(row, rowEdits);
      if (changes.length === 0) return;
      count += changes.length;
      result.push({ id, title: row.title || '(untitled)', changes });
    });
    result.sort((a, b) => a.title.localeCompare(b.title));
    return { recordings: result, fieldChangeCount: count };
  }, [edits, rows]);

  const failedCount = recordings.filter(
    (r) => (errorsByRecording?.[r.id]?.length ?? 0) > 0
  ).length;
  const hasFailures = failedCount > 0 || !!fatalError;

  return (
    <WModal
      isOpen={isOpen}
      onClose={onClose}
      title="Review mass edit"
      modalClasses="tw-w-[820px] tw-max-w-[97vw]"
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <p className="tw-text-sm tw-text-textDove">
          {recordings.length === 0 ? (
            'No changes to apply.'
          ) : hasFailures ? (
            <>
              <strong>{failedCount}</strong> recording
              {failedCount === 1 ? '' : 's'} could not be saved. The changes
              below are still pending — fix them and confirm again, or remove
              them.
            </>
          ) : (
            <>
              You are about to apply <strong>{fieldChangeCount}</strong> field
              change{fieldChangeCount === 1 ? '' : 's'} across{' '}
              <strong>{recordings.length}</strong> recording
              {recordings.length === 1 ? '' : 's'}.
            </>
          )}
        </p>

        {fatalError && (
          <div className="tw-rounded-xl tw-border tw-border-red-700/60 tw-bg-red-900/20 tw-p-3">
            <p className="tw-text-xs tw-text-red-200">{fatalError}</p>
          </div>
        )}

        {recordings.length > 0 && (
          <div className="tw-max-h-[55vh] tw-overflow-y-auto tw-flex tw-flex-col tw-gap-4 tw-pr-1">
            {recordings.map((rec) => {
              const recErrors = errorsByRecording?.[rec.id] ?? [];
              return (
                <div
                  key={rec.id}
                  className={clsx(
                    'tw-rounded-xl tw-border tw-bg-surfaceBlack90 tw-p-4',
                    recErrors.length > 0
                      ? 'tw-border-red-700/70'
                      : 'tw-border-gray-700'
                  )}
                >
                  <h4 className="tw-text-white tw-text-sm tw-font-semibold tw-mb-3">
                    {rec.title}
                  </h4>
                  {recErrors.length > 0 && (
                    <ul className="tw-mb-3 tw-flex tw-flex-col tw-gap-1">
                      {recErrors.map((message) => (
                        <li
                          key={message}
                          className="tw-text-xs tw-text-red-300 tw-flex tw-gap-1.5"
                        >
                          <AlertCircle className="tw-w-3.5 tw-h-3.5 tw-shrink-0 tw-mt-px" />
                          <span>{message}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-sm">
                      <thead>
                        <tr className="tw-text-left tw-text-textDove tw-border-b tw-border-gray-700">
                          <th className="tw-py-1.5 tw-pr-3 tw-font-medium tw-w-[150px]">
                            Field
                          </th>
                          <th className="tw-py-1.5 tw-pr-3 tw-font-medium">
                            Before
                          </th>
                          <th className="tw-py-1.5 tw-pr-3 tw-font-medium">
                            After
                          </th>
                          <th className="tw-py-1.5 tw-font-medium tw-w-[96px] tw-text-right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rec.changes.map((c) => (
                          <tr
                            key={c.label}
                            className="tw-border-b tw-border-gray-800 tw-align-top last:tw-border-0"
                          >
                            <td className="tw-py-2 tw-pr-3 tw-text-textDove">
                              {c.label}
                            </td>
                            <td className="tw-py-2 tw-pr-3 tw-text-textSeal tw-line-through">
                              {c.before}
                            </td>
                            <td className="tw-py-2 tw-pr-3 tw-text-white">
                              {c.after}
                            </td>
                            <td className="tw-py-2">
                              <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                                <button
                                  type="button"
                                  aria-label={`Edit ${c.label}`}
                                  title="Edit change"
                                  disabled={isSaving}
                                  className="tw-p-1 tw-rounded hover:tw-bg-gray-700 tw-text-textDove hover:tw-text-white disabled:tw-opacity-50"
                                  onClick={() => onEditChange(rec.id, c.field)}
                                >
                                  <Pencil className="tw-w-3.5 tw-h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  aria-label={`Remove ${c.label} change`}
                                  title="Remove change"
                                  disabled={isSaving}
                                  className="tw-p-1 tw-rounded hover:tw-bg-gray-700 tw-text-textDove hover:tw-text-white disabled:tw-opacity-50"
                                  onClick={() =>
                                    onRemoveChange(rec.id, c.field)
                                  }
                                >
                                  <X className="tw-w-3.5 tw-h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-2">
          <WButton
            variant="secondary"
            label="Back"
            disabled={isSaving}
            onClick={onClose}
          />
          <WButton
            variant="primary"
            label={
              isSaving
                ? 'Saving…'
                : `${
                    hasFailures ? 'Retry' : 'Confirm'
                  } ${fieldChangeCount} change${
                    fieldChangeCount === 1 ? '' : 's'
                  }`
            }
            disabled={recordings.length === 0 || isSaving}
            onClick={onConfirm}
          />
        </div>
      </div>
    </WModal>
  );
};

export default MassEditSummaryModal;
